import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { geoAzimuthalEquidistant, geoPath, geoGraticule } from 'd3-geo';
import { feature } from 'topojson-client';
import { locatorToLatLon } from '../lib/locator.js';
import { bearingDeg, distanceKm } from '../lib/geo.js';
import { bandColor } from '../lib/bands.js';
import { nightPolygon, graylinePolygon, graylineInner } from '../lib/night.js';

const ATLAS_URL = '/world-atlas/land-110m.json';

let atlasPromise;
function loadAtlas() {
  if (!atlasPromise) {
    atlasPromise = fetch(ATLAS_URL)
      .then((r) => r.json())
      .then((topo) => feature(topo, topo.objects.land));
  }
  return atlasPromise;
}

const MIN_RADIUS = 1000;
const MAX_RADIUS = 20000;
const clampRadius = (km) => Math.max(MIN_RADIUS, Math.min(MAX_RADIUS, km));

// Map palette per theme. The dark set is the original night-sky look;
// the light set is a paper-style cartography (pale ocean, slightly
// darker land, faint graticule). Centre pin + hover arc stay cyan in
// both via the .map-accent-* classes — cyan reads on both substrates.
const MAP_PALETTE = {
  dark: {
    canvas:     '#0b1220',
    ocean:      '#0f172a',
    graticule:  '#1e293b',
    landFill:   '#1e293b',
    landStroke: '#334155',
    night:      'rgba(0,0,0,0.45)',
    grayline:   'rgba(255, 200, 80, 0.18)',
    rim:        '#334155',
    tickMinor:  '#475569',
    tickMajor:  '#64748b',
    cardinal:   '#cbd5e1',
    deg:        '#94a3b8',
  },
  light: {
    canvas:     '#e2e8f0', // slate-200
    ocean:      '#f1f5f9', // slate-100
    graticule:  '#cbd5e1', // slate-300
    landFill:   '#cbd5e1', // slate-300
    landStroke: '#94a3b8', // slate-400
    night:      'rgba(15, 23, 42, 0.18)',  // subtle dark wash on light bg
    grayline:   'rgba(251, 191, 36, 0.40)', // amber-400, denser to read on light
    rim:        '#94a3b8',
    tickMinor:  '#cbd5e1',
    tickMajor:  '#94a3b8',
    cardinal:   '#334155', // slate-700
    deg:        '#64748b', // slate-500
  },
};

export default function Map({
  spots, locators, settings, alertIds,
  selectedId, onSelect,
  hoverId, onHover,
  onUpdate,
}) {
  const ref = useRef(null);
  const [size, setSize] = useState({ w: 800, h: 800 });
  const [land, setLand] = useState(null);
  const [now, setNow] = useState(() => new Date());

  const p = MAP_PALETTE[settings.theme === 'light' ? 'light' : 'dark'];

  // Pinch / wheel zoom state. settings.radiusKm is the persisted source of
  // truth; during a gesture we read the freshest value from a ref to avoid
  // stale closures.
  const radiusRef = useRef(settings.radiusKm);
  useEffect(() => { radiusRef.current = settings.radiusKm; }, [settings.radiusKm]);

  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 60_000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    loadAtlas().then(setLand).catch((e) => console.error('atlas', e));
  }, []);

  useEffect(() => {
    if (!ref.current) return;
    const el = ref.current;
    const ro = new ResizeObserver(() => {
      const r = el.getBoundingClientRect();
      setSize({ w: Math.max(1, Math.floor(r.width)), h: Math.max(1, Math.floor(r.height)) });
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // Wheel / trackpad-pinch zoom. Plain wheel zooms over the map; we
  // preventDefault so the page doesn't scroll. Trackpad pinch on Chrome /
  // Safari fires wheel events with ctrlKey=true; same handling.
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const onWheel = (e) => {
      e.preventDefault();
      // Negative deltaY = scroll up = zoom in (smaller radius). Exponential
      // scaling so each notch is a constant ratio.
      const factor = Math.exp(e.deltaY * 0.0015);
      const next = clampRadius(radiusRef.current * factor);
      if (next === radiusRef.current) return;
      radiusRef.current = next;
      onUpdate({ radiusKm: Math.round(next / 50) * 50, autoFit: false });
    };
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, [onUpdate]);

  // Touch pinch zoom. Track active pointers; while exactly two are down,
  // measure the distance between them and scale radius by the ratio between
  // successive frames.
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    // globalThis.Map — the local component identifier is `Map` and would
    // shadow the global constructor here.
    const pointers = new globalThis.Map();
    let lastDist = null;

    const dist = () => {
      const [a, b] = [...pointers.values()];
      const dx = a.x - b.x, dy = a.y - b.y;
      return Math.hypot(dx, dy);
    };

    const onDown = (e) => {
      if (e.pointerType !== 'touch') return;
      pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });
      if (pointers.size === 2) lastDist = dist();
    };
    const onMove = (e) => {
      if (!pointers.has(e.pointerId)) return;
      pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });
      if (pointers.size === 2 && lastDist) {
        e.preventDefault();
        const d = dist();
        if (d <= 0) return;
        // Pulling fingers apart shrinks the visible radius (zoom in).
        const next = clampRadius(radiusRef.current * (lastDist / d));
        radiusRef.current = next;
        onUpdate({ radiusKm: Math.round(next / 50) * 50, autoFit: false });
        lastDist = d;
      }
    };
    const onUp = (e) => {
      pointers.delete(e.pointerId);
      if (pointers.size < 2) lastDist = null;
    };

    el.addEventListener('pointerdown', onDown);
    el.addEventListener('pointermove', onMove, { passive: false });
    el.addEventListener('pointerup', onUp);
    el.addEventListener('pointercancel', onUp);
    el.addEventListener('pointerleave', onUp);
    return () => {
      el.removeEventListener('pointerdown', onDown);
      el.removeEventListener('pointermove', onMove);
      el.removeEventListener('pointerup', onUp);
      el.removeEventListener('pointercancel', onUp);
      el.removeEventListener('pointerleave', onUp);
    };
  }, [onUpdate]);

  const { projection, path, centre, discR, cx, cy } = useMemo(() => {
    let centreLatLon = [45.35, 7.69];
    try {
      centreLatLon = locatorToLatLon(settings.locator);
    } catch { /* fallback */ }
    // Reserve ~18 px around the rim when degree labels are on, room for
    // tick marks (3 / 6 px) and cardinal letters (~12 px tall).
    const margin = settings.showDegrees ? 18 : 0;
    const r = Math.max(1, Math.min(size.w, size.h) / 2 - margin);
    const cx = size.w / 2, cy = size.h / 2;
    const proj = geoAzimuthalEquidistant()
      .rotate([-centreLatLon[1], -centreLatLon[0]])
      .clipAngle(180 - 1e-3)
      .translate([cx, cy])
      .scale(r / (settings.radiusKm / 6371));
    return { projection: proj, path: geoPath(proj), centre: centreLatLon, discR: r, cx, cy };
  }, [settings.locator, settings.radiusKm, size, settings.showDegrees]);

  const graticule = useMemo(() => geoGraticule().step([15, 15])(), []);

  const spotMarkers = useMemo(() => {
    const out = [];
    for (const s of spots) {
      const ll = pickLatLon(s, locators);
      if (!ll) continue;
      const xy = projection(ll);
      if (!xy || !isFinite(xy[0]) || !isFinite(xy[1])) continue;
      out.push({ s, x: xy[0], y: xy[1] });
    }
    return out;
  }, [spots, locators, projection]);

  const centreXY = projection([centre[1], centre[0]]);
  const nightPath = useMemo(() => path(nightPolygon(now)), [path, now]);
  const graylineOuterPath = useMemo(() => path(graylinePolygon(now, 2)), [path, now]);
  const graylineInnerPath = useMemo(() => path(graylineInner(now, 2)), [path, now]);

  const hoveredSpot = useMemo(() => {
    if (!hoverId) return null;
    return spotMarkers.find((p) => p.s.id === hoverId)?.s || null;
  }, [hoverId, spotMarkers]);

  // Card target: hover wins on desktop (live preview); selection is the
  // mobile fallback since touch has no hover. Tap on a circle toggles
  // selection via onSelect, so the card appears/dismisses on tap.
  const infoSpot = useMemo(() => {
    if (hoveredSpot) return hoveredSpot;
    if (!selectedId) return null;
    return spotMarkers.find((p) => p.s.id === selectedId)?.s || null;
  }, [hoveredSpot, selectedId, spotMarkers]);

  // Great-circle line from centre to whichever spot the card is showing.
  const infoArcD = useMemo(() => {
    if (!infoSpot) return null;
    const ll = pickLatLon(infoSpot, locators);
    if (!ll) return null;
    return path({ type: 'LineString', coordinates: [[centre[1], centre[0]], ll] });
  }, [infoSpot, locators, centre, path]);

  const infoBearing = useMemo(() => {
    if (!infoSpot) return null;
    const ll = pickLatLon(infoSpot, locators);
    if (!ll) return null;
    return bearingDeg(centre[0], centre[1], ll[1], ll[0]);
  }, [infoSpot, locators, centre]);

  // Spotter→DX great-circle lines (one per spot with both endpoints).
  // Rendered behind the spot markers when settings.showLinks is on.
  const linkPaths = useMemo(() => {
    if (!settings.showLinks) return null;
    const out = [];
    for (const s of spots) {
      const dx = pickLatLon(s, locators);
      const sp = pickSpotterLatLon(s, locators);
      if (!dx || !sp) continue;
      const d = path({ type: 'LineString', coordinates: [sp, dx] });
      if (d) out.push({ id: s.id, d, band: s.band });
    }
    return out;
  }, [settings.showLinks, spots, locators, path]);

  // Zoom buttons: mirror the wheel handler's exponential ratio so
  // each tap roughly equals 4 wheel notches. Same clamp + 50-km snap.
  // Manual zoom always cancels auto-fit so the user wins.
  const zoomBy = useCallback((factor) => {
    const next = clampRadius(radiusRef.current * factor);
    if (next === radiusRef.current) return;
    radiusRef.current = next;
    onUpdate({ radiusKm: Math.round(next / 50) * 50, autoFit: false });
  }, [onUpdate]);

  // Auto-fit math: smallest 50-km-snapped radius that holds every
  // visible spot, with 15% headroom. Returns null when nothing fits yet.
  const computeAutoRadius = useCallback(() => {
    let maxKm = 0;
    for (const s of spots) {
      const ll = pickLatLon(s, locators);
      if (!ll) continue;
      const d = distanceKm(centre[0], centre[1], ll[1], ll[0]);
      if (d > maxKm) maxKm = d;
    }
    if (maxKm <= 0) return null;
    return clampRadius(Math.round(maxKm * 1.15 / 50) * 50);
  }, [spots, locators, centre]);

  // Toggle handler: turning auto-fit on triggers an immediate fit;
  // turning it off just freezes the current radius.
  const toggleAutoFit = useCallback(() => {
    if (settings.autoFit) {
      onUpdate({ autoFit: false });
      return;
    }
    const next = computeAutoRadius();
    if (next != null) {
      radiusRef.current = next;
      onUpdate({ radiusKm: next, autoFit: true });
    } else {
      onUpdate({ autoFit: true });
    }
  }, [settings.autoFit, onUpdate, computeAutoRadius]);

  // Continuous refit while auto-fit is on. The 50-km snap inside
  // computeAutoRadius is the main damper: most new spots fall inside
  // the current bucket and produce no state change → no re-render.
  useEffect(() => {
    if (!settings.autoFit) return;
    const next = computeAutoRadius();
    if (next == null || next === radiusRef.current) return;
    radiusRef.current = next;
    onUpdate({ radiusKm: next });
  }, [settings.autoFit, computeAutoRadius, onUpdate]);

  const onContext = (s) => (e) => {
    e.preventDefault();
    window.open(`https://www.qrz.com/db/${encodeURIComponent(s.dx_callsign)}`, '_blank', 'noopener,noreferrer');
  };

  return (
    <div ref={ref} className="w-full h-full bg-slate-950 relative overflow-hidden touch-none">
      <svg width={size.w} height={size.h} className="block absolute inset-0">
        {/* Whole canvas filled with the same ocean color so the disc sits
            inside it without visible "side room". */}
        <rect x="0" y="0" width={size.w} height={size.h} fill={p.canvas} />
        {/* Clip everything outside the disc — graticule/land/night/grayline
            shouldn't bleed into the canvas margins. */}
        <defs>
          <clipPath id="disc-clip">
            <circle cx={cx} cy={cy} r={discR - 1} />
          </clipPath>
        </defs>
        <g clipPath="url(#disc-clip)">
          <circle cx={cx} cy={cy} r={discR - 1} fill={p.ocean} />
          <path d={path(graticule)} fill="none" stroke={p.graticule} strokeWidth="0.5" />
          {land && <path d={path(land)} fill={p.landFill} stroke={p.landStroke} strokeWidth="0.4" />}
          {nightPath && <path d={nightPath} fill={p.night} />}
          {graylineOuterPath && graylineInnerPath && (
            <path d={graylineOuterPath + ' ' + graylineInnerPath}
                  fillRule="evenodd"
                  fill={p.grayline} />
          )}
          {[2500, 5000, 7500, 10000].map((km) =>
            km <= settings.radiusKm && (
              <circle key={km}
                      cx={cx} cy={cy}
                      r={discR * (km / settings.radiusKm)}
                      fill="none" stroke={p.rim} strokeDasharray="2,3" strokeWidth="0.5" />
            )
          )}
        </g>
        {/* Disc outline drawn outside the clip so the rim is crisp. */}
        <circle cx={cx} cy={cy} r={discR - 0.5} fill="none" stroke={p.rim} strokeWidth="1" />
        {/* Azimuth rose: minor ticks every 10°, major ticks + labels
            every 30°. Cardinals N/E/S/W replace the numbers at the
            four cardinal positions (slightly larger + bolder). */}
        {settings.showDegrees && (
          <g>
            {Array.from({ length: 36 }, (_, i) => {
              const deg = i * 10;
              if (deg % 30 === 0) return null;
              const rad = (deg * Math.PI) / 180;
              const x1 = cx + (discR + 1) * Math.sin(rad);
              const y1 = cy - (discR + 1) * Math.cos(rad);
              const x2 = cx + (discR + 3) * Math.sin(rad);
              const y2 = cy - (discR + 3) * Math.cos(rad);
              return (
                <line key={`m${deg}`} x1={x1} y1={y1} x2={x2} y2={y2}
                      stroke={p.tickMinor} strokeWidth="0.5" />
              );
            })}
            {Array.from({ length: 12 }, (_, i) => {
              const deg = i * 30;
              const rad = (deg * Math.PI) / 180;
              const x1 = cx + (discR + 1) * Math.sin(rad);
              const y1 = cy - (discR + 1) * Math.cos(rad);
              const x2 = cx + (discR + 6) * Math.sin(rad);
              const y2 = cy - (discR + 6) * Math.cos(rad);
              const lr = discR + 13;
              const tx = cx + lr * Math.sin(rad);
              const ty = cy - lr * Math.cos(rad);
              const cardinal = { 0: 'N', 90: 'E', 180: 'S', 270: 'W' }[deg];
              return (
                <g key={deg}>
                  <line x1={x1} y1={y1} x2={x2} y2={y2}
                        stroke={p.tickMajor} strokeWidth="0.7" />
                  <text x={tx} y={ty}
                        textAnchor="middle"
                        dominantBaseline="middle"
                        fontFamily="monospace"
                        fontSize={cardinal ? 12 : 10}
                        fontWeight={cardinal ? 600 : 400}
                        fill={cardinal ? p.cardinal : p.deg}>
                    {cardinal || deg}
                  </text>
                </g>
              );
            })}
          </g>
        )}
        {/* Spots, hover-arc, and centre pin are clipped to the disc so points
            beyond the configured radius don't render in the corners. */}
        <g clipPath="url(#disc-clip)">
        {centreXY && (
          <g>
            <circle cx={centreXY[0]} cy={centreXY[1]} r="5" fill="#22d3ee" className="map-accent-fill" />
            <circle cx={centreXY[0]} cy={centreXY[1]} r="9" fill="none" stroke="#22d3ee" opacity="0.5" className="map-accent-stroke" />
          </g>
        )}
        {linkPaths && linkPaths.map((l) => (
          <path key={l.id} d={l.d} fill="none" stroke={bandColor(l.band)} strokeWidth="0.7" opacity="0.35" />
        ))}
        {infoArcD && <path d={infoArcD} fill="none" stroke="#22d3ee" strokeWidth="1.2" opacity="0.7" className="map-accent-stroke" />}
        {spotMarkers.map(({ s, x, y }) => {
          const isAlert = alertIds.has(s.id);
          const isSel = s.id === selectedId;
          const isHover = s.id === hoverId;
          return (
            <circle
              key={s.id}
              cx={x} cy={y}
              r={isSel || isHover ? 6 : isAlert ? 4 : 3}
              fill={bandColor(s.band)}
              stroke={isAlert ? '#fbbf24' : isSel || isHover ? '#fff' : 'none'}
              strokeWidth={isAlert ? 2 : 1.5}
              style={{ cursor: 'pointer' }}
              onClick={() => onSelect(s.id === selectedId ? null : s.id)}
              onMouseEnter={() => onHover(s.id)}
              onMouseLeave={() => onHover(null)}
              onContextMenu={onContext(s)}
            >
              <title>{`${s.dx_callsign} · ${s.frequency} kHz · ${s.band}m ${s.mode}`}</title>
            </circle>
          );
        })}
        </g>
      </svg>

      {/* Zoom / fit / link-toggle toolbar — top-right. */}
      <div className="absolute top-2 right-2 flex flex-col gap-1 font-mono text-xs select-none">
        <ToolBtn label="+"    title="Zoom in"                     onClick={() => zoomBy(0.8)} />
        <ToolBtn label="−"    title="Zoom out"                    onClick={() => zoomBy(1.25)} />
        <ToolBtn label="auto" title="Auto-fit (toggle): keep all spots in view"
                 active={settings.autoFit}
                 onClick={toggleAutoFit} />
        <ToolBtn label="✻"    title="Toggle spotter→DX links"
                 active={settings.showLinks}
                 onClick={() => onUpdate({ showLinks: !settings.showLinks })} />
        <ToolBtn label="°"    title="Toggle azimuth degrees"
                 active={settings.showDegrees}
                 onClick={() => onUpdate({ showDegrees: !settings.showDegrees })} />
      </div>

      {/* Spot info corner — hover on desktop, tap-select on mobile. */}
      {infoSpot && (
        <div className="absolute bottom-2 left-2 bg-slate-900/90 px-3 py-2 rounded text-xs font-mono text-slate-200 pointer-events-none border border-slate-700">
          <div className="font-bold text-base">{infoSpot.dx_callsign}</div>
          <div className="text-slate-400">
            QRG {infoSpot.frequency} kHz · {infoSpot.band}m · {infoSpot.mode}
          </div>
          <div className="text-slate-400">
            {infoSpot.dx_country} ({infoSpot.dx_continent})
            {infoBearing != null && <> · Az {String(Math.round(infoBearing)).padStart(3, '0')}°</>}
          </div>
          {infoSpot.comment && (
            <div className="text-slate-500 max-w-xs truncate">{infoSpot.comment}</div>
          )}
          <div className="text-slate-500 mt-1">de {infoSpot.spotter_callsign}</div>
        </div>
      )}
    </div>
  );
}

function ToolBtn({ label, title, active, onClick }) {
  const base = 'w-9 h-9 rounded border flex items-center justify-center';
  const cls = active
    ? `${base} bg-cyan-600/80 border-cyan-400 text-white`
    : `${base} bg-slate-900/80 border-slate-700 text-slate-200 hover:bg-slate-800`;
  return (
    <button type="button" title={title} onClick={onClick} className={cls}>
      {label}
    </button>
  );
}

function pickLatLon(s, locators) {
  if (locators) {
    const cl = locators.get(s.dx_callsign);
    if (cl && (cl.lat || cl.lon)) return [cl.lon, cl.lat];
  }
  if (s.dx_lat || s.dx_lon) return [s.dx_lon, s.dx_lat];
  if (s.dx_locator) {
    try {
      const [lat, lon] = locatorToLatLon(s.dx_locator);
      return [lon, lat];
    } catch { /* unparseable */ }
  }
  return null;
}

function pickSpotterLatLon(s, locators) {
  if (locators && s.spotter_callsign) {
    const cl = locators.get(s.spotter_callsign);
    if (cl && (cl.lat || cl.lon)) return [cl.lon, cl.lat];
  }
  if (s.spotter_lat || s.spotter_lon) return [s.spotter_lon, s.spotter_lat];
  if (s.spotter_locator) {
    try {
      const [lat, lon] = locatorToLatLon(s.spotter_locator);
      return [lon, lat];
    } catch { /* unparseable */ }
  }
  return null;
}
